import * as React from 'react';
import styled from 'styled-components';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { useSelector } from 'react-redux';

import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { PageHeader } from 'ui/pageHeader';
import { AddNew16, Export24, Overflow24 } from 'ui/icons';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button/constants';
import { IconButton, PrimaryButton } from 'ui/button';
import { spacing } from 'ui/theme';

const { useMemo } = React;

const displayText = {
  newWorkflowButton: 'New journey',
  exportCsvButton: 'Export data to CSV',
  pageTitle: 'Journeys',
};

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.half};
`;
type CreationButtonProps = {
  onCreationButtonClick: () => void;
};

type ExportCsvButtonProps = {
  onExportCsvButtonClick: () => void;
};

type WorkflowIndexHeaderProps = CreationButtonProps & ExportCsvButtonProps;

const getOverflowMenuOptions = ({
  onExportCsvButtonClick,
}: ExportCsvButtonProps): ActionMenuOption[] => [
  {
    as: AllowedActionMenuElements.Button,
    content: (
      <>
        <Export24 isColorInherited={true} />
        <div>{displayText.exportCsvButton}</div>
      </>
    ),
    onClick: onExportCsvButtonClick,
    id: 'export-csv',
  },
];

export const WorkflowIndexHeader = ({
  onCreationButtonClick,
  onExportCsvButtonClick,
}: WorkflowIndexHeaderProps) => {
  const options = useMemo(
    () => getOverflowMenuOptions({ onExportCsvButtonClick }),
    [onExportCsvButtonClick],
  );
  const canCreateNewWorkflow = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );

  return (
    <PageHeader
      data-test="workflow-index-page-header"
      pageActions={
        <ButtonContainer>
          <ActionMenu
            id="workflow-index-page-overflow-menu"
            align={MENU_ALIGNMENTS.RIGHT}
            options={options}
          >
            <IconButton size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.SECONDARY}>
              <Overflow24 />
            </IconButton>
          </ActionMenu>
          <PrimaryButton
            onClick={onCreationButtonClick}
            isDisabled={!canCreateNewWorkflow}
            size={BUTTON_SIZES.LARGE}
          >
            <AddNew16 />
            <span>{displayText.newWorkflowButton}</span>
          </PrimaryButton>
        </ButtonContainer>
      }
    >
      {displayText.pageTitle}
    </PageHeader>
  );
};
